import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Flex,
  Heading,
  Box,
  Stack,
  Button,
  IconButton,
  Icon,
  Container,
  Text,
} from '@chakra-ui/react';
import {
  ArrowLeftIcon,
  HeartIcon,
  ShareIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import PrintButton from '@/components/ui/print-button';
import { LoginButton } from '@/components/ui/login-button';
import PrintFieldSelector from '@/components/ui/print-field-selector';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import Carousel from '@/components/carousel';
import BlockContentType from '@/components/content/block-content-type';
import ContentAuthor from '@/components/content/content-author';
import CommentSection from '@/components/comments/comment-section';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { useAuth } from '@/lib/hooks/use-auth';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import {
  Content,
  ContentTypeFieldView,
  Favorite,
  File,
  FileType,
  BlockType,
  StaticField,
} from '@/lib/types';

import '@/styles/print.css';
import { slotFor } from '@/lib/slot-color';

interface Props {
  data: {
    content: Content;
    subContents: any;
    contentTypeFields: ContentTypeFieldView[];
    workspaceCreatorEmail: string;
    workspaceName: string;
    contentCreatorName?: string;
  };
  isPublic?: boolean;
  inDrawer?: boolean;
  onClose?: () => void;
  autoOpenPrintDialog?: boolean;
}

interface FieldInfo {
  key: string;
  type: BlockType;
  label: string;
  options: Map<string, string>;
}

const getFileType = (fileUrl: string): FileType.Image | FileType.Video => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
  const extension = fileUrl.split('.').pop()?.toLowerCase();

  if (extension && videoExtensions.includes(extension)) {
    return FileType.Video;
  }
  return FileType.Image;
};

const createFieldInfo = (
  field: ContentTypeFieldView,
  includeOptions: boolean = false,
): FieldInfo => {
  let options = new Map<string, string>();

  if (includeOptions && field.field_type === BlockType.Selection) {
    if (field.field_options && field.field_options.length > 0) {
      options = field.field_options.reduce((map, item) => {
        map.set(item.option_value, item.option_label);
        return map;
      }, new Map<string, string>());
    }
  }

  return {
    key: field.referenceID,
    type: field.field_type,
    label: field.field_name,
    options: options,
  };
};

export default function ViewContent({
  data,
  isPublic = false,
  inDrawer = false,
  onClose,
  autoOpenPrintDialog = false,
}: Props) {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [printableFields, setPrintableFields] = useState<string[]>([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();
  const { currentUser } = useAuth();
  const deleteModal = useModalToggle();
  const [favorite, setFavorite] = useState<Favorite | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [isDeleting, setIsDeleting] = useState(false);

  const contentId = data?.content?.id;
  const contentRefId = data?.content?.reference_id;
  const contentTypeId = data?.content?.content_type_id;
  const contentName = data?.content?.name;
  const contentStatus = data?.content?.status;
  const contentWorkspaceId = data?.content?.workspace_id;
  const contentPublicLink = data?.content?.public_link;

  const canManage =
    !isPublic &&
    (isAdmin || isUser) &&
    !!workspaceId &&
    parseInt(workspaceId, 10) === contentWorkspaceId;
  const canFavorite = !isPublic && !!currentUser?.id && !!contentId;
  const isDraft = contentStatus !== 'published';
  const setLoading = (key: string, value: boolean) =>
    setActionLoading((prev) => ({ ...prev, [key]: value }));

  const fetchFavorite = useCallback(async () => {
    if (!canFavorite) {
      setFavorite(null);
      return;
    }
    try {
      const res = await api.get(`favorites/list/${currentUser?.id}`);
      const list: Favorite[] = res.data.data || [];
      const match = list.find((f) => f.workspace_content_id === contentId) || null;
      setFavorite(match);
    } catch {
      setFavorite(null);
    }
  }, [canFavorite, currentUser?.id, contentId]);

  useEffect(() => {
    fetchFavorite();
  }, [fetchFavorite]);

  useEffect(() => {
    if (autoOpenPrintDialog) setPrintDialogOpen(true);
  }, [autoOpenPrintDialog]);

  const handleFavorite = async () => {
    if (!canFavorite || !contentId) return;
    setLoading('favorite', true);
    try {
      if (favorite) {
        await api.delete(`favorites/${favorite.id}`);
        toaster.create({ type: 'success', title: 'Removed from favorites' });
      } else {
        await api.post('favorites', {
          user_id: currentUser?.id,
          workspace_content_id: contentId,
        });
        toaster.create({ type: 'success', title: 'Added to favorites' });
      }
      await fetchFavorite();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update favorites',
      });
    } finally {
      setLoading('favorite', false);
    }
  };

  const handleSharePublicLink = async () => {
    if (!contentId) return;
    setLoading('share', true);
    try {
      let fallback: string | undefined = contentPublicLink || undefined;
      if (!contentPublicLink) {
        const res = await api.put(`contents/public/${contentId}`);
        fallback = res.data?.data?.public_link;
      }
      const link = (contentRefId && buildPublicUrl('content', contentRefId)) || fallback;
      if (link) {
        await navigator.clipboard.writeText(link);
        toaster.create({ type: 'success', title: 'Public link copied to clipboard' });
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to share content',
      });
    } finally {
      setLoading('share', false);
    }
  };

  const handleEdit = () => {
    if (!contentId || !contentTypeId || !workspaceId) return;
    if (onClose) onClose();
    navigate(`/${workspaceId}/content-types/${contentTypeId}/contents/${contentId}/edit`);
  };

  const handleDuplicate = async () => {
    if (!contentId) return;
    setLoading('duplicate', true);
    try {
      await api.post(`contents/duplicate/${contentId}`);
      toaster.create({ type: 'success', title: 'Content duplicated' });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to duplicate content',
      });
    } finally {
      setLoading('duplicate', false);
    }
  };

  const handleChangeStatus = async () => {
    if (!contentId) return;
    const next = isDraft ? 'published' : 'draft';
    setLoading('status', true);
    try {
      await api.patch(`contents/status/${contentId}/${next}`);
      toaster.create({
        type: 'success',
        title: next === 'published' ? 'Shared to workspace' : 'Hidden from workspace',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update status',
      });
    } finally {
      setLoading('status', false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!contentId) return;
    setIsDeleting(true);
    try {
      await api.delete(`contents/${contentId}`);
      toaster.create({ type: 'success', title: 'Content removed' });
      deleteModal.close();
      if (onClose) onClose();
      else navigate(-1);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete content',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!data?.content?.media_files?.files) {
      setFiles([]);
      return;
    }

    const mediaFiles = data.content.media_files.files;
    const processedFiles: File[] = mediaFiles.map((file: string) => ({
      type: getFileType(file),
      src: file,
    }));

    setFiles(processedFiles);
  }, [data.content.media_files]);

  useEffect(() => {
    if (!data?.content?.content || !data?.contentTypeFields) {
      setFields([]);
      return;
    }

    const content = { ...data.content };
    const contentTypeFields = [...(data.contentTypeFields || [])];

    if (content.description) {
      content.content[StaticField.Description] = {
        value: content.description,
      };
    }

    const contentRefIds = new Set(Object.keys(content.content || {}));

    const sortedFields = [...contentTypeFields].sort((a, b) => a.sort_order - b.sort_order);

    const processedFields = sortedFields.reduce<FieldInfo[]>((result, field) => {
      if (field.field_type === BlockType.SectionTitle) {
        result.push(createFieldInfo(field));
        return result;
      }

      if (field.field_unique_name === StaticField.Description) {
        field.referenceID = field.field_unique_name;
      }

      if (field.referenceID && contentRefIds.has(field.referenceID)) {
        result.push(createFieldInfo(field, true));
      }

      return result;
    }, []);

    setFields(processedFields);
  }, [data]);

  const handlePrintWithFields = (selectedFields: string[]) => {
    setPrintableFields(selectedFields);

    setTimeout(() => {
      const source = document.querySelector('[data-print-target]');
      if (!source) {
        window.print();
        return;
      }

      document.querySelector('#ae-print-clone')?.remove();
      const clone = document.createElement('div');
      clone.id = 'ae-print-clone';
      clone.innerHTML = source.innerHTML;
      document.body.appendChild(clone);
      document.body.classList.add('ae-printing');

      let cleanedUp = false;
      const printMql = window.matchMedia('print');
      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        clone.remove();
        document.body.classList.remove('ae-printing');
        setPrintableFields([]);
        // Defensive: if Zag's dismissable layer cleanup got interrupted by
        // window.print(), the body can be left with data-inert + inline
        // pointer-events: none, blocking all clicks/scrolls. Clear them.
        if (document.body.hasAttribute('data-inert')) {
          document.body.removeAttribute('data-inert');
          document.body.style.pointerEvents = '';
          if (document.body.style.length === 0) {
            document.body.removeAttribute('style');
          }
        }
        window.removeEventListener('afterprint', cleanup);
        printMql.removeEventListener('change', mqlListener);
      };
      const mqlListener = (e: MediaQueryListEvent) => {
        if (!e.matches) cleanup();
      };
      window.addEventListener('afterprint', cleanup);
      printMql.addEventListener('change', mqlListener);

      window.print();
    }, 300);
  };

  const fieldOptions = fields.map((field) => ({
    id: field.key,
    label: field.label,
    type: BlockType[field.type] || 'Other',
  }));

  if (files.length > 0) {
    fieldOptions.unshift({
      id: 'media-files',
      label: 'Images & Videos',
      type: 'Media',
    });
  }

  const shouldPrintField = (fieldId: string): boolean => {
    return printableFields.length === 0 || printableFields.includes(fieldId);
  };

  const { content, subContents } = data || { content: {}, subContents: {} };

  const selectionFields = useMemo(
    () => fields.filter((f) => f.type === BlockType.Selection),
    [fields],
  );
  const metaItems = useMemo(() => {
    return selectionFields
      .map((field) => {
        const raw = content?.content?.[field.key]?.value;
        const values = Array.isArray(raw)
          ? raw.map((item: string) => field.options.get(item) ?? item)
          : [];
        if (values.length === 0) return null;
        return {
          key: field.key,
          label: field.label,
          values,
          value: values.join(', '),
          slot: slotFor(field.label || 'field'),
          initial: (field.label || '?').trim().charAt(0).toUpperCase(),
        };
      })
      .filter(Boolean) as Array<{
        key: string;
        label: string;
        values: string[];
        value: string;
        slot: number;
        initial: string;
      }>;
  }, [selectionFields, content]);
  const otherFields = useMemo(() => {
    const remaining = fields.filter((f) => f.type !== BlockType.Selection);
    // Drop SectionTitle dividers that have no remaining content beneath them
    // (e.g. a "Tags" section whose only field was a Selection promoted to pills above).
    return remaining.filter((field, idx) => {
      if (field.type !== BlockType.SectionTitle) return true;
      const next = remaining[idx + 1];
      return !!next && next.type !== BlockType.SectionTitle;
    });
  }, [fields]);

  const isDisplayComment =
    (isAdmin || isUser) && workspaceId && parseInt(workspaceId, 10) === content?.workspace_id;

  const contentTypeName = content?.content_type_name;

  return (
    <Container
      maxW={inDrawer ? 'full' : '5xl'}
      px={inDrawer ? 0 : { base: 0, md: 4 }}
      py={inDrawer ? 0 : { base: 2, md: 4 }}
      className='print-container'
    >
      {/* Top bar — back, breadcrumb, actions */}
      <Flex
        align='center'
        gap={3}
        mb={4}
        wrap='wrap'
        className='no-print'
        px={{ base: 4, md: 0 }}
      >
        {!inDrawer && (
          <Button type='button' variant='ghost' size='sm' onClick={() => navigate(-1)}>
            <Icon as={ArrowLeftIcon} w={4} h={4} />
            Back
          </Button>
        )}
        {contentTypeName && (
          <Breadcrumbs items={[{ label: 'Library' }, { label: contentTypeName }]} />
        )}
      </Flex>

      {/* Content card */}
      <Box
        bg={inDrawer ? 'transparent' : 'bg.panel'}
        borderWidth={inDrawer ? 0 : { base: 0, md: 1 }}
        borderColor='border'
        borderRadius={inDrawer ? 0 : { base: 0, md: 'lg' }}
        shadow={inDrawer ? 'none' : { base: 'none', md: 'sm' }}
        p={inDrawer ? 0 : { base: 5, md: 8 }}
        className='print-card'
      >
        <Heading
          as='h1'
          fontSize={{ base: 'h2', md: 'h1' }}
          lineHeight={1.2}
        >
          {content?.name}
        </Heading>

        <Box mt={4}>
          <ContentAuthor
            name={data?.contentCreatorName || content?.created_user}
            updatedAt={content?.updated_at}
          />
        </Box>

        <Stack gap={6} mt={6}>
          {files.length > 0 ? (
            <Box
              borderRadius='md'
              overflow='hidden'
              className={`media-container ${!shouldPrintField('media-files') ? 'no-print' : ''}`}
            >
              <Carousel items={files} />
            </Box>
          ) : (
            <Flex
              borderRadius='md'
              h='200px'
              bg='fg.strong'
              align='center'
              justify='center'
              className='no-print'
            >
              <Text fontSize='sm' color='fg.muted' fontWeight='500'>
                No media available
              </Text>
            </Flex>
          )}

          {(() => {
            const chipFields = metaItems.filter((item) => item.values.length <= 3);
            const listFields = metaItems.filter((item) => item.values.length > 3);
            const chipsAllUnprintable = chipFields.every((f) => !shouldPrintField(f.key));
            const listAllUnprintable = listFields.every((f) => !shouldPrintField(f.key));
            return (
              <Stack gap={4}>
                {chipFields.length > 0 && (
                  <Flex
                    gap={3}
                    flexWrap='wrap'
                    className={`field-container ${chipsAllUnprintable ? 'no-print' : ''}`}
                  >
                    {chipFields.map((item) => (
                      <Box
                        key={item.key}
                        borderWidth={1}
                        borderColor='border'
                        bg={`slot${item.slot}.bg`}
                        borderRadius='md'
                        px={3}
                        py={2}
                      >
                        <Text
                          textStyle='eyebrow'
                          lineHeight={1.2}
                          mb={1}
                        >
                          {item.label}
                        </Text>
                        <Text fontSize='ui' fontWeight='semibold' color={`slot${item.slot}.fg`} lineHeight={1.3}>
                          {item.value}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                )}

                {listFields.length > 0 && (
                  <Box
                    as='dl'
                    display='grid'
                    gridTemplateColumns='auto auto 1fr'
                    columnGap={3}
                    rowGap={0}
                    m={0}
                    className={`field-container ${listAllUnprintable ? 'no-print' : ''}`}
                  >
                    {listFields.map((item, idx) => {
                      const isLast = idx === listFields.length - 1;
                      const rowBorder = isLast ? 0 : 1;
                      return (
                        <Fragment key={item.key}>
                          <Box
                            py={3}
                            display='flex'
                            alignItems='center'
                            justifyContent='center'
                            borderBottomWidth={rowBorder}
                            borderColor='border.muted'
                          >
                            <Box
                              w={7}
                              h={7}
                              borderRadius='md'
                              bg={`slot${item.slot}.bg`}
                              color={`slot${item.slot}.fg`}
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              fontSize='xs'
                              fontWeight='bold'
                              flexShrink={0}
                            >
                              {item.initial}
                            </Box>
                          </Box>
                          <Box
                            as='dt'
                            py={3}
                            display='flex'
                            alignItems='center'
                            borderBottomWidth={rowBorder}
                            borderColor='border.muted'
                            fontSize='label'
                            textTransform='uppercase'
                            letterSpacing='wider'
                            color='fg.muted'
                            fontWeight='semibold'
                            whiteSpace='nowrap'
                          >
                            {item.label}
                          </Box>
                          <Box
                            as='dd'
                            m={0}
                            py={3}
                            display='flex'
                            alignItems='center'
                            borderBottomWidth={rowBorder}
                            borderColor='border.muted'
                            fontSize='sm'
                            color='fg'
                            fontWeight='medium'
                          >
                            {item.value}
                          </Box>
                        </Fragment>
                      );
                    })}
                  </Box>
                )}
              </Stack>
            );
          })()}

          {/* Action buttons — placed below metadata, above body sections */}
          <Flex
            gap={2}
            wrap='wrap'
            pb={6}
            borderBottomWidth='1px'
            borderColor='border.muted'
            className='no-print'
            align='center'
          >
            {isPublic ? (
              <>
                <PrintFieldSelector
                  fields={fieldOptions}
                  onPrint={handlePrintWithFields}
                  buttonText='Custom Print'
                />
                <PrintButton documentTitle={content?.name} />
                <LoginButton />
              </>
            ) : (
              <>
                {canFavorite && (
                  <Tooltip content={favorite ? 'Remove from favorites' : 'Add to favorites'}>
                    <IconButton
                      type='button'
                      aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
                      size='sm'
                      variant='ghost'
                      color={favorite ? 'danger' : 'fg.muted'}
                      loading={!!actionLoading.favorite}
                      onClick={handleFavorite}
                    >
                      <Icon as={favorite ? HeartSolidIcon : HeartIcon} w={4} h={4} />
                    </IconButton>
                  </Tooltip>
                )}
                {!isPublic && (
                  <Button
                    type='button'
                    size='sm'
                    variant='secondary'
                    loading={!!actionLoading.share}
                    onClick={handleSharePublicLink}
                  >
                    <Icon as={ShareIcon} w={4} h={4} />
                    Share
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button
                      type='button'
                      size='sm'
                      variant='secondary'
                      loading={!!actionLoading.status}
                      onClick={handleChangeStatus}
                    >
                      <Icon as={isDraft ? EyeIcon : EyeSlashIcon} w={4} h={4} />
                      {isDraft ? 'Publish' : 'Unpublish'}
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      variant='secondary'
                      loading={!!actionLoading.duplicate}
                      onClick={handleDuplicate}
                    >
                      <Icon as={DocumentDuplicateIcon} w={4} h={4} />
                      Duplicate
                    </Button>
                    <Button type='button' size='sm' variant='secondary' onClick={handleEdit}>
                      <Icon as={PencilIcon} w={4} h={4} />
                      Edit
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      variant='danger'
                      onClick={deleteModal.open}
                    >
                      <Icon as={TrashIcon} w={4} h={4} />
                      Delete
                    </Button>
                  </>
                )}
                <PrintFieldSelector
                  fields={fieldOptions}
                  onPrint={handlePrintWithFields}
                  buttonText='Print'
                  open={printDialogOpen}
                  onOpenChange={setPrintDialogOpen}
                />
              </>
            )}
          </Flex>

          {otherFields.map((field) => {
            const printable = shouldPrintField(field.key);
            const containerClass = `field-container ${printable ? 'print-field' : 'no-print'}`;

            const fieldEntry = content?.content?.[field.key];
            const fieldContent =
              field.type === BlockType.Link ? fieldEntry : fieldEntry?.value || '';

            // SectionTitle blocks already render their own heading + divider.
            if (field.type === BlockType.SectionTitle) {
              return (
                <Box key={field.key} className={containerClass} pt={2}>
                  <BlockContentType
                    block={field}
                    content={fieldContent}
                    subContents={subContents}
                    isPublic={isPublic}
                  />
                </Box>
              );
            }

            return (
              <Box key={field.key} className={containerClass}>
                <Text
                  textStyle='eyebrow'
                  mb={3}
                  pb={2}
                  borderBottomWidth={1}
                  borderColor='border'
                >
                  {field.label}
                </Text>
                <BlockContentType
                  block={field}
                  content={fieldContent}
                  subContents={subContents}
                  isPublic={isPublic}
                  hideLabel={true}
                />
              </Box>
            );
          })}
        </Stack>

        {isDisplayComment && !isPublic && (
          <Box mt={8} className='no-print'>
            <CommentSection contentId={content?.id} />
          </Box>
        )}
      </Box>

      <DestructiveModal
        isOpen={deleteModal.isOpen}
        onCancel={deleteModal.close}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title='Delete content'
        confirmText='Delete'
      >
        Are you sure you want to delete <b>{contentName}</b>? It will also be removed from any
        practice plans and curriculums that use it. This cannot be undone.
      </DestructiveModal>
    </Container>
  );
}
